import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Camera, ExternalLink, X } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';
import { Skeleton } from '@/src/shadComponents/ui/skeleton';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

interface CommandCenterCardProps {
    activationId: number;
    activationName: string;
    photo: MediaInstance | null;
    photoCount: number;
    isLoading: boolean;
    onRemove: (id: number) => void;
}

export default function CommandCenterCard({
    activationId,
    activationName,
    photo,
    photoCount,
    isLoading,
    onRemove,
}: CommandCenterCardProps) {
    const navigate = useNavigate();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();
    const [galleryUrl, setGalleryUrl] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchGalleryUrl = async () => {
            try {
                const galleries = await getGalleriesForActivation(activationId);
                if (!isMounted || !galleries[0]) return;

                const domain = await getOrgDomain(org?.id);
                if (!isMounted) return;

                const cleanDomain = domain?.endsWith('/')
                    ? domain.slice(0, -1)
                    : domain;
                const slug = encodeURIComponent(galleries[0].slug);
                setGalleryUrl(
                    cleanDomain
                        ? `${cleanDomain}/gallery/${slug}`
                        : `/gallery/${slug}`,
                );
            } catch {
                // Gallery URL is optional
            }
        };

        fetchGalleryUrl();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const timeAgo = photo?.createdAt
        ? formatDistanceToNow(new Date(photo.createdAt), { addSuffix: true })
        : null;

    if (isLoading) {
        return (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="p-3 border-b border-border">
                    <Skeleton className="h-5 w-3/4" />
                </div>
                <Skeleton className="aspect-square w-full" />
                <div className="p-3 space-y-2">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-8 w-full" />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-border bg-card overflow-hidden group">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <a
                    href={`/activations/${activationId}`}
                    className="text-sm font-medium text-foreground truncate hover:underline"
                >
                    {activationName}
                </a>
                <button
                    onClick={() => onRemove(activationId)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    title="Remove from Command Center"
                >
                    <X className="size-3.5" />
                </button>
            </div>

            {/* Photo */}
            {photo ? (
                <div
                    className="relative aspect-square cursor-pointer overflow-hidden"
                    onClick={() =>
                        navigate(`/activations/${activationId}#media`)
                    }
                >
                    <img
                        src={photo.thumbnail_800pxUri || photo.thumbnailUri}
                        alt={activationName}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                    />
                    {/* Device name overlay */}
                    {photo.deviceKeyId && (
                        <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                            {photo.deviceKeyId}
                        </span>
                    )}
                    {/* Timestamp overlay */}
                    {timeAgo && (
                        <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                            {timeAgo}
                        </span>
                    )}
                </div>
            ) : (
                <div className="aspect-square bg-muted/50 flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                        <Camera className="size-8 mx-auto mb-2 opacity-40" />
                        <p className="text-xs">No photos yet</p>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="px-3 py-2 flex items-center justify-between">
                <button
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={() =>
                        navigate(`/activations/${activationId}#media`)
                    }
                >
                    <Camera className="size-3" />
                    {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
                </button>
                {galleryUrl && (
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => window.open(galleryUrl, '_blank')}
                    >
                        <ExternalLink className="size-3 mr-1" />
                        Gallery
                    </Button>
                )}
            </div>
        </div>
    );
}
